import { Environment, fetchQuery, graphql } from 'react-relay';
import { Dispatch } from 'redux';
import { getBuyerId } from 'dibs-cookie-jar';
import { localStorage } from 'dibs-browser-storage';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';

import isPageType from '../../utils/isPageType';

import { actionCreators } from '../../actions';
import {
    shouldSuppressMessage,
    shouldSeeConsumerSourcingTooltip,
} from '../../components/PdpRespConsumerSourcingTooltip/helpers/pdpSharedConsumerSourcingTooltipDisplayHelpers';
import { STORAGE_KEY_PDP_VISITS } from '../../components/PdpRespConsumerSourcingTooltip/helpers/pdpSharedConsumerSourcingHelpersConstants';
import { logSourcingPdpVisit } from '../../components/PdpRespConsumerSourcingTooltip/helpers/pdpSharedConsumerSourcingEvaluationHelpers';

import { consumerSourcingTooltipQuery } from './__generated__/consumerSourcingTooltipQuery.graphql';

const SOURCING_TOOLTIP_PRIORITY = 400;

const getRuleData = (): { isAllowedPage: boolean; userId: string; itemId: string } => {
    const isAllowedPage = isPageType([PAGE_TYPE.PDP]);
    const userId = getBuyerId(document.cookie) || '';
    const pdpVisits = localStorage.getItem(STORAGE_KEY_PDP_VISITS) || [];
    const itemId = Object.keys(pdpVisits[0] || {})[0];

    return { isAllowedPage, userId, itemId };
};

const consumerSourcingTooltip = async ({
    relayEnvironment,
}: {
    relayEnvironment: Environment;
}): Promise<boolean> => {
    const { isAllowedPage, userId, itemId } = getRuleData();
    if (!isAllowedPage || !userId || !itemId || shouldSuppressMessage({ userId })) {
        return false;
    }
    const shouldShowSourcingTooltip = await shouldSeeConsumerSourcingTooltip({
        environment: relayEnvironment,
        userId,
        itemId,
    });

    return shouldShowSourcingTooltip;
};

const itemPriceQuery = graphql`
    query consumerSourcingTooltipQuery($itemId: String!) {
        viewer {
            item(itemId: $itemId) {
                pricing {
                    retailPrice {
                        convertedAmounts {
                            USD
                        }
                    }
                }
            }
        }
    }
`;

const consumerSourcingTooltipLoggingConfig = {
    /**
     * `consumerSourcingTooltipLogging` rule is being used
     * in combination with `consumerSourcingTooltip`.
     * It logs unique PDP visits which are being used by `consumerSourcingTooltip`.
     * It has to be updated/removed together with `consumerSourcingTooltip` rule.
     * Should always resolve with `false`.
     * `priority` should be higher than `consumerSourcingTooltip`, so
     * it runs before `consumerSourcingTooltip` rule
     */
    name: 'consumerSourcingTooltipLogging',
    rule: async ({ relayEnvironment }: { relayEnvironment: Environment }) => {
        const { isAllowedPage, userId, itemId } = getRuleData();
        /**
         * Do not fetch any additional data and log PDP visits when:
         *  1. not on allowed page
         *  2. user is not logged in
         *  3. no item id
         *  4. user has suppressed tooltip (aka clicked "Don't show again")
         */
        if (isAllowedPage && userId && itemId && !shouldSuppressMessage({ userId })) {
            const response = await fetchQuery<consumerSourcingTooltipQuery>(
                relayEnvironment,
                itemPriceQuery,
                {
                    itemId,
                }
            );
            const itemPrice =
                response?.viewer?.item?.pricing?.retailPrice?.convertedAmounts?.USD || 0;

            logSourcingPdpVisit({
                itemId,
                userId,
                itemPrice,
            });
        }

        // Always resolve with false, as this rule is used for logging only
        return false;
    },
    // Proprity should be increased by 1 so it runs
    // right before `consumerSourcingTooltip` rule
    priority: SOURCING_TOOLTIP_PRIORITY + 1,
};

export const consumerSourcingTooltipConfig = [
    consumerSourcingTooltipLoggingConfig,
    {
        name: 'consumerSourcingTooltip',
        rule: consumerSourcingTooltip,
        action: () => (dispatch: Dispatch) => {
            dispatch(actionCreators.showConsumerSourcingTooltip());
        },
        priority: SOURCING_TOOLTIP_PRIORITY,
    },
];
